import { useEffect, useState, useRef } from 'react';
import { memoryMonitor, TRPC_SAFETY_CONFIG } from '@/lib/trpcSafetyConfig';

/**
 * Query Monitor Component
 * Tracks active queries, memory pressure, and circuit breaker status
 * Automatically throttles or pauses queries when pressure is detected
 */

interface QueryStats {
  activeQueries: number;
  failedQueries: number;
  memoryPressure: 'normal' | 'high' | 'critical';
  circuitBreakerOpen: boolean;
  requestsPerSecond: number;
}

let globalStats: QueryStats = {
  activeQueries: 0,
  failedQueries: 0,
  memoryPressure: 'normal',
  circuitBreakerOpen: false,
  requestsPerSecond: 0,
};

const requestTimestamps: number[] = [];
let circuitBreakerFailures = 0;

/**
 * Track query execution
 */
export function trackQueryStart() {
  globalStats.activeQueries++;
  requestTimestamps.push(Date.now());

  // Cleanup old timestamps (older than 1 second)
  const now = Date.now();
  while (requestTimestamps.length > 0 && now - requestTimestamps[0] > 1000) {
    requestTimestamps.shift();
  }

  globalStats.requestsPerSecond = requestTimestamps.length;

  // Check if we need to activate circuit breaker
  if (globalStats.activeQueries > TRPC_SAFETY_CONFIG.throttle.MAX_CONCURRENT_QUERIES) {
    // Circuit breaker activated
  }
}

/**
 * Track query completion
 */
export function trackQueryEnd(success: boolean) {
  globalStats.activeQueries = Math.max(0, globalStats.activeQueries - 1);

  if (!success) {
    circuitBreakerFailures++;
    globalStats.failedQueries++;

    if (circuitBreakerFailures >= TRPC_SAFETY_CONFIG.circuitBreaker.FAILURE_THRESHOLD) {
      globalStats.circuitBreakerOpen = true;

      // Reset after timeout
      setTimeout(() => {
        globalStats.circuitBreakerOpen = false;
        circuitBreakerFailures = 0;
      }, TRPC_SAFETY_CONFIG.circuitBreaker.RESET_TIMEOUT_MS);
    }
  } else {
    circuitBreakerFailures = Math.max(0, circuitBreakerFailures - 1);
  }
}

/**
 * Get current query statistics
 */
export function getQueryStats(): QueryStats {
  return { ...globalStats };
}

/**
 * Check if queries should be throttled
 */
export function shouldThrottleQueries(): boolean {
  return (
    globalStats.circuitBreakerOpen ||
    globalStats.memoryPressure === 'critical' ||
    globalStats.activeQueries > TRPC_SAFETY_CONFIG.throttle.MAX_CONCURRENT_QUERIES ||
    globalStats.requestsPerSecond > TRPC_SAFETY_CONFIG.throttle.MAX_REQUESTS_PER_SECOND
  );
}

/**
 * Query Monitor Display Component (for debugging)
 */
export function QueryMonitorDisplay() {
  const [stats, setStats] = useState<QueryStats>(globalStats);
  const [showDebug, setShowDebug] = useState(false);
  const monitorRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Start memory monitor
    memoryMonitor.start(2000);

    // Listen to memory pressure changes
    const unsubscribe = memoryMonitor.onPressureChange((pressure) => {
      globalStats.memoryPressure = pressure;
      setStats({ ...globalStats });

      if (pressure === 'critical') {
      } else if (pressure === 'high') {
      }
    });

    // Update stats every 500ms
    monitorRef.current = setInterval(() => {
      setStats({ ...globalStats });
    }, 500);

    return () => {
      memoryMonitor.stop();
      unsubscribe();
      if (monitorRef.current) clearInterval(monitorRef.current);
    };
  }, []);

  if (!showDebug) {
    return null;
  }

  const memoryColor =
    stats.memoryPressure === 'critical' ? 'bg-red-500' : stats.memoryPressure === 'high' ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="fixed bottom-4 right-4 bg-slate-900 text-white p-4 rounded-lg shadow-lg text-xs font-mono z-50 max-w-xs">
      <div className="flex justify-between items-center mb-2">
        <span className="font-bold">Query Monitor</span>
        <button
          onClick={() => setShowDebug(false)}
          className="text-xs bg-slate-700 hover:bg-slate-600 px-2 py-1 rounded"
        >
          ✕
        </button>
      </div>

      <div className="space-y-1 text-xs">
        <div>Active Queries: {stats.activeQueries}</div>
        <div>Failed Queries: {stats.failedQueries}</div>
        <div>Requests/sec: {stats.requestsPerSecond}</div>
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${memoryColor}`} />
          <span>Memory: {stats.memoryPressure.toUpperCase()}</span>
        </div>
        <div>
          Circuit Breaker:{' '}
          <span className={stats.circuitBreakerOpen ? 'text-red-400' : 'text-green-400'}>
            {stats.circuitBreakerOpen ? 'OPEN' : 'CLOSED'}
          </span>
        </div>
      </div>

      <div className="mt-2 pt-2 border-t border-slate-700 text-xs text-slate-400">
        <p>Throttling: {shouldThrottleQueries() ? 'YES' : 'NO'}</p>
      </div>
    </div>
  );
}

// Make debug toggle accessible via console
if (typeof window !== 'undefined') {
  (window as any).__queryMonitorDebug = () => {
    const elem = document.querySelector('[data-query-monitor]');
    if (elem) {
      const button = elem.querySelector('button');
      if (button) button.click();
    }
  };
}
